import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import {
  Star,
  Clock,
  X,
  ArrowRight,
  BarChart3,
  FileBarChart2,
  Percent,
  CreditCard,
  User,
  CheckSquare,
  Receipt,
  ShoppingCart,
  Package,
  LayoutGrid,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import usePermissions from '../auth/usePermissions';
import '../styles/Reports.css';

const DEFAULT_FAVORITE_PATHS = [
  '/reports/profit-by-project',
  '/reports/client-payment',
  '/reports/sales-tax-report',
];

const RECENT_LIMIT = 20;
const RECENT_VISIBLE = 5;

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function formatRelativeDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Reports() {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = usePermissions();

  const isRootReports = location.pathname === '/reports';
  const userId = localStorage.getItem('userId') || 'anon';
  const favoritesKey = `reportsFavorites_${userId}`;
  const recentKey = `reportsRecent_${userId}`;

  const [favorites, setFavorites] = useState(() => loadJSON(favoritesKey, DEFAULT_FAVORITE_PATHS));
  const [recent, setRecent] = useState(() => loadJSON(recentKey, []));
  const [editingFavorites, setEditingFavorites] = useState(false);
  const [showAllRecent, setShowAllRecent] = useState(false);

  useEffect(() => {
    localStorage.setItem(favoritesKey, JSON.stringify(favorites));
  }, [favorites, favoritesKey]);

  useEffect(() => {
    localStorage.setItem(recentKey, JSON.stringify(recent));
  }, [recent, recentKey]);

  const standardReports = [
    {
      key: 'reportProfitByProject',
      label: 'Profit by Project',
      path: '/reports/profit-by-project',
      description: 'View profitability across every project.',
      icon: BarChart3,
    },
    {
      key: 'reportProfitDetail',
      label: 'Profit Detail by Project',
      path: '/reports/profit-detail',
      description: 'Detailed profit analysis by project and phase.',
      icon: FileBarChart2,
    },
    {
      key: 'reportTimeBilling',
      label: 'Time Billing Hub',
      path: '/reports/time-billing-report',
      description: 'Company-wide time across every project, filterable by employee, project and billable status.',
      icon: Clock,
    },
    {
      key: 'reportClientPayment',
      label: 'Client Payment Report',
      path: '/reports/client-payment',
      description: 'Track payments, deposits and outstanding balances.',
      icon: CreditCard,
    },
    {
      key: 'reportVendorPayment',
      label: 'Vendor Payment Report',
      path: '/reports/vendor-payment',
      description: 'View payments made to vendors and suppliers.',
      icon: User,
    },
    {
      key: 'reportARAging',
      label: 'A/R Aging',
      path: '/reports/ar-aging',
      description: 'Outstanding client balances by client and age.',
      icon: TrendingUp,
    },
    {
      key: 'reportAPAging',
      label: 'A/P Aging',
      path: '/reports/ap-aging',
      description: 'Outstanding vendor balances by vendor and age.',
      icon: TrendingDown,
    },
    {
      key: 'reportSalesTax',
      label: 'Sales Tax Report',
      path: '/reports/sales-tax-report',
      description: 'View taxable sales and filing summaries.',
      icon: Percent,
    },
    {
      key: 'reportTaskCompletion',
      label: 'Task Completion Report',
      path: '/reports/task-report',
      description: 'Track task progress and completion by project.',
      icon: CheckSquare,
    },
    {
      key: 'reportTimeBilling',
      label: 'Time Billing Invoice Report',
      path: '/reports/tb-invoice-report',
      description: 'Review invoiced time billing by client.',
      icon: Receipt,
    },
  ];

  const procurementReports = [
    {
      key: 'reportPurchaseOrderStatus',
      label: 'Procurement Tracker',
      path: '/reports/procurement-tracker',
      description: 'Track purchase orders and procurement status.',
      icon: ShoppingCart,
    },
    {
      key: 'reportItemTracker',
      label: 'Item Tracker',
      path: '/reports/item-tracker',
      description: 'Monitor inventory and item details.',
      icon: Package,
    },
    {
      key: 'reportRoomByRoom',
      label: 'Room by Room Item List',
      path: '/reports/room-by-room',
      description: 'View items and selections organized by room.',
      icon: LayoutGrid,
    },
  ];

  const visibleStandard = standardReports.filter((r) => permissions[r.key] !== false);
  const visibleProcurement = procurementReports.filter((r) => permissions[r.key] !== false);

  // Deduplicate Time Billing entries sharing the same permission key
  const seen = new Set();
  const dedupedStandard = visibleStandard.filter((r) => {
    const dedupKey = r.key + r.path;
    if (seen.has(dedupKey)) return false;
    seen.add(dedupKey);
    return true;
  });

  const allReports = useMemo(
    () => [...dedupedStandard, ...visibleProcurement],
    [dedupedStandard, visibleProcurement]
  );

  const reportsByPath = useMemo(() => {
    const map = new Map();
    allReports.forEach((r) => map.set(r.path, r));
    return map;
  }, [allReports]);

  const favoriteReports = favorites.map((path) => reportsByPath.get(path)).filter(Boolean);
  const recentEntries = recent.filter((r) => reportsByPath.has(r.path));
  const visibleRecent = showAllRecent
    ? recentEntries.slice(0, RECENT_LIMIT)
    : recentEntries.slice(0, RECENT_VISIBLE);

  const toggleFavorite = (path, e) => {
    e.stopPropagation();
    setFavorites((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const removeFavorite = (path, e) => {
    e.stopPropagation();
    setFavorites((prev) => prev.filter((p) => p !== path));
  };

  const openReport = (report) => {
    setRecent((prev) => {
      const withoutThis = prev.filter((r) => r.path !== report.path);
      return [
        { path: report.path, label: report.label, ranAt: new Date().toISOString() },
        ...withoutThis,
      ].slice(0, RECENT_LIMIT);
    });
    navigate(report.path);
  };

  const renderCard = (report) => {
    const Icon = report.icon;
    const isFavorite = favorites.includes(report.path);
    return (
      <div
        key={report.path}
        className="report-card"
        onClick={() => openReport(report)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') openReport(report);
        }}
      >
        <div className="report-card-top">
          <div className="report-card-icon">
            <Icon size={18} />
          </div>
          <button
            type="button"
            className={`report-card-star ${isFavorite ? 'is-favorite' : ''}`}
            onClick={(e) => toggleFavorite(report.path, e)}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star size={14} />
          </button>
        </div>
        <div className="report-card-title">{report.label}</div>
        <div className="report-card-desc">{report.description}</div>
        <div className="report-card-bottom">
          <span className="report-card-link">
            View Report <ArrowRight size={13} />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="reports-container">
      {isRootReports && (
        <>
          <div className="reports-layout">
            <div className="reports-main">
              {dedupedStandard.length > 0 && (
                <section className="report-section">
                  <h2 className="section-title">Standard Reports</h2>
                  <p className="section-description">
                    Financial reporting and project performance.
                  </p>
                  <div className="report-grid">{dedupedStandard.map(renderCard)}</div>
                </section>
              )}

              {visibleProcurement.length > 0 && (
                <section className="report-section">
                  <h2 className="section-title">Procurement Reports</h2>
                  <p className="section-description">Purchasing, vendor tracking and inventory.</p>
                  <div className="report-grid">{visibleProcurement.map(renderCard)}</div>
                </section>
              )}
            </div>

            <aside className="reports-sidebar">
              <div className="reports-panel">
                <div className="reports-panel-header">
                  <h3>
                    <Star size={14} className="panel-icon" /> Favorite Reports
                  </h3>
                  {favoriteReports.length > 0 && (
                    <button
                      type="button"
                      className="panel-edit-btn"
                      onClick={() => setEditingFavorites((v) => !v)}
                    >
                      {editingFavorites ? 'Done' : 'Edit'}
                    </button>
                  )}
                </div>
                <ul className="reports-panel-list">
                  {favoriteReports.length === 0 && (
                    <li className="panel-empty">Star a report to pin it here.</li>
                  )}
                  {favoriteReports.map((r) => {
                    const Icon = r.icon;
                    return (
                      <li key={r.path} className="panel-item" onClick={() => openReport(r)}>
                        <Icon size={14} className="panel-item-icon" />
                        <span className="panel-item-label">{r.label}</span>
                        {editingFavorites ? (
                          <button
                            type="button"
                            className="panel-remove-btn"
                            onClick={(e) => removeFavorite(r.path, e)}
                            aria-label="Remove favorite"
                          >
                            <X size={12} />
                          </button>
                        ) : (
                          <Star size={13} className="panel-item-star" />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="reports-panel">
                <div className="reports-panel-header">
                  <h3>
                    <Clock size={14} className="panel-icon" /> Recently Run
                  </h3>
                  {recentEntries.length > RECENT_VISIBLE && (
                    <button
                      type="button"
                      className="panel-edit-btn"
                      onClick={() => setShowAllRecent((v) => !v)}
                    >
                      {showAllRecent ? 'Show less' : 'View all'}
                    </button>
                  )}
                </div>
                <ul className="reports-panel-list">
                  {visibleRecent.length === 0 && (
                    <li className="panel-empty">Run a report to see it here.</li>
                  )}
                  {visibleRecent.map((entry) => {
                    const report = reportsByPath.get(entry.path);
                    const Icon = report?.icon || Clock;
                    return (
                      <li
                        key={entry.path}
                        className="panel-item"
                        onClick={() => report && openReport(report)}
                      >
                        <Icon size={14} className="panel-item-icon" />
                        <span className="panel-item-label">{entry.label}</span>
                        <span className="panel-item-date">{formatRelativeDate(entry.ranAt)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </aside>
          </div>
        </>
      )}

      {/* Child report pages render here */}
      <Outlet />
    </div>
  );
}
